import * as _ from "lodash";
import DataGenerator from "@/shared/DataGenerator";
import {INodeModel} from "@/shared/models/abstract/INodeModel";
import {IEdgeModel} from "@/shared/models/abstract/IEdgeModel";
import ModelFactory from "@/shared/models/modelFactory";
import {IRootModel} from "@/shared/models/abstract/IRootModel";
import RecentStack from "@/store/recentStack";
import {BackendType} from "@/shared/DataService/BackendType";

/**
 * Defines an local knowledge graph.
 */
export class Plex {
    /**
     * This nodes collection is only used if the backend is the local web storage.
     */
    public nodes: INodeModel[];

    /**
     * This edges collection is only used if the backend is the local web storage.
     */
    public edges: IEdgeModel[];

    /**
     * The recently used items within this plex.
     */
    public recent: string[];

    /**
     * Instantiates a {@link Plex}.
     * @param name {string} The name of the plex.
     * @param description {string} A description of what the plex is about.
     * @param [id] {string} Optional id of the plex. If not specified a uuid will be generated.
     * @param origin {BackendType} The origin of this plex.
     */
    constructor(public name: string, public description: string = "", public id: string = null, public origin: BackendType | string = BackendType.LocalStore) {
        if (_.isNil(id)) {
            this.id = DataGenerator.id();
        }
        this.nodes = [];
        this.edges = [];
        this.recent = [];
    }


    public toJson() {
        return {
            id: this.id,
            name: this.name,
            description: this.description,
            nodes: this.nodes.map(n => n.toJson()),
            edges: this.edges.map(e => e.toJson()),
            recent: this.recent,
            origin: this.origin
        }
    }

    public static fromString(s: string): Plex {
        const json = JSON.parse(s);
        return Plex.fromJson(json);
    }

    public static fromJson(json: any) {
        const name = json.name;
        const description = json.description;
        const id = json.id;
        const origin = json.origin;
        const recentIds = _.clone(json.recent);
        if (_.isNil(name)) {
            throw new Error("Missing the name in serialized plex.");
        }
        if (_.isNil(id)) {
            throw new Error("Missing the id in serialized plex.");
        }
        let p = new Plex(name, description, id, origin);
        p.recent = recentIds;
        if (origin === BackendType.LocalStore) { // all the data sits in the serialized plex
            for (let i = 0; i < json.nodes.length; i++) {
                const item = json.nodes[i];
                let model: INodeModel;
                try {
                    model = ModelFactory.fromJson(item) as INodeModel;
                    if (!_.isNil(model)) {
                        p.nodes.push(model);
                    }
                } catch (e) {
                    console.error(`Failed to deserialize a model of type '${model.entityType}'.`)
                }
            }

            for (let i = 0; i < json.edges.length; i++) {
                const item = json.edges[i];
                let model: IEdgeModel;
                try {
                    model = ModelFactory.fromJson(item) as IEdgeModel;
                    if (!_.isNil(model)) {
                        p.edges.push(model);
                    }
                } catch (e) {
                    console.error(`Failed to deserialize a model of type '${model.entityType}'.`)
                }
            }
        }

        return p;
    }

}
